import { DialogPositioningBehavior, DialogType, DialogWidget } from '@tableau/widgets';
import * as React from 'react';
import { Query } from 'react-apollo';

import { Messages } from '../../../gen-src/Localize';
import { FieldDetailsFragment } from '../../../gen-src/queries/FieldDetailsFragment';
import { LensFieldDetailsQuery, LensFieldDetailsQueryVariables } from '../../../gen-src/queries/LensFieldDetailsQuery';
import { LensFieldDetailsQueryGql } from '../../queries/LensFieldDetailsQueryGql';
import { FieldDetailsFieldName, FieldDetailsPane, FieldDetailsProperties, FieldDetailsTypeRow, renderTypeSpecificNonDatasourceFieldDetails } from '../FieldDetails/FieldDetailsDialog';
import { ConnectedDatasourceField, FieldPropertyDescription } from '../FieldDetails/FieldPropertyComponents';
import { SvgFieldIcon } from '../field/components/SvgFieldIcon';
import { iconForField } from '../field/functions/FieldFunctions';
import { FieldNameMappings } from '../field/functions/FieldNameMapping';
import { renderQueryResult } from '../renderQueryResult';

const dialogWidth = 500;

export interface LensFieldDetailsDialogProps {
  readonly fieldId: string;
  readonly makeFieldUrl: (fieldId: string) => string;
  readonly onClose: () => void;
  readonly lensLuid: string;
}

export class LensFieldDetailsDialog extends React.Component<LensFieldDetailsDialogProps> {

  public render(): JSX.Element {
    return (
      <Query<LensFieldDetailsQuery, LensFieldDetailsQueryVariables>
        query={LensFieldDetailsQueryGql}
        variables={{ lensFieldId: this.props.fieldId }}
      >
        {result => renderQueryResult(result, { renderData: this.renderLensFieldDetails })}
      </Query>
    );
  }

  private readonly renderLensFieldDetails = (data: LensFieldDetailsQuery): React.ReactNode => {
    const field = data.lensFields && data.lensFields[0];
    if (!field) {
      return null;
    }
    const fieldIcon = iconForField(field.datasourceField);

    const datasource = field?.datasourceField?.datasource;
    const datasourceVizPortalUrlId = datasource?.__typename === 'PublishedDatasource' ? datasource?.vizportalId : null;

    return (
      <DialogWidget
        testId='field-details'
        title={Messages.FieldDetails()}
        type={DialogType.NormalWithTitleBar}
        positioningBehavior={DialogPositioningBehavior.Centered}
        dialogWidth={dialogWidth}
        showCloseButton={true}
        showFooter={false}
        isModal={true}
        onCloseRequest={this.props.onClose}
        handleGlassClick={this.props.onClose}
      >
        <FieldDetailsPane>
          <FieldDetailsFieldName>{field.name || field.datasourceField.name}</FieldDetailsFieldName>
          <FieldDetailsTypeRow>
            <SvgFieldIcon fieldIcon={fieldIcon} />
            {fieldIcon ? FieldNameMappings[fieldIcon]() : null}
          </FieldDetailsTypeRow>
          <FieldDetailsProperties>
            <FieldPropertyDescription labelFunc={Messages.Description} description={field.description || field.datasourceField.description} />
            {this.renderTypeSpecificFieldDetails(field.datasourceField)}
            <ConnectedDatasourceField
              datasourceVizPortalUrlId={datasourceVizPortalUrlId}
              fieldId={field?.datasourceField.id}
              fieldName={field?.datasourceField?.name} />
          </FieldDetailsProperties>
        </FieldDetailsPane>
      </DialogWidget>
    );
  }

  private renderTypeSpecificFieldDetails(field: FieldDetailsFragment): React.ReactNode {
    if (field.__typename !== 'DatasourceField') {
      return renderTypeSpecificNonDatasourceFieldDetails(field, this.props.makeFieldUrl);
    } else if (field.remoteField) {
      return renderTypeSpecificNonDatasourceFieldDetails(field.remoteField, this.props.makeFieldUrl);
    } else {
      return null;
    }
  }
}
